import React, { useEffect, useState } from 'react';

import superagent from 'superagent';

import { backendUrl } from '../../../helpers/consts.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog.jsx';
import { Button } from '@shadcn/components/ui/button.jsx';
import { Input } from '@shadcn/components/ui/input.jsx';
import { Label } from '@shadcn/components/ui/label.jsx';

// 12 months matches the engine's own cover window, so the default reproduces the
// behaviour rates already had before anyone picks a narrower window.
const WINDOW_PRESETS = [3, 6, 12];
const DEFAULT_PRESET_MONTHS = 12;

const isoDay = (date) => date.toISOString().slice(0, 10);

/**
 * The window ending today that spans the last `months` months, as YYYY-MM-DD bounds.
 * @param {number} months
 * @returns {{ from: string, to: string }}
 */
const presetWindow = (months) => {
  const to = new Date();
  const from = new Date(to);
  from.setMonth(from.getMonth() - months);
  return { from: isoDay(from), to: isoDay(to) };
};

/**
 * Pick a window and recompute COGS across it. Rates come back stamped with the window
 * they were averaged over, so the table's Window column reflects what was just run.
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} props.onRecalculated - re-run the rates query with the new figures
 */
const RecalculateCogsDialog = ({ open, onClose, onRecalculated }) => {
  const [range, setRange] = useState(() => presetWindow(DEFAULT_PRESET_MONTHS));
  const [state, setState] = useState('idle');

  // Reset each time it opens, so a previous narrow window isn't re-run by accident.
  useEffect(() => {
    if (open) {
      setRange(presetWindow(DEFAULT_PRESET_MONTHS));
      setState('idle');
    }
  }, [open]);

  const activePreset = WINDOW_PRESETS.find((months) => {
    const preset = presetWindow(months);
    return preset.from === range.from && preset.to === range.to;
  });

  const handleRecalculate = () => {
    setState('loading');
    superagent
      .post(`${backendUrl}/cogs/recompute-window`)
      .query({ from: range.from, to: range.to })
      .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
      .set('accept', 'json')
      .then((response) => {
        const { containers = 0, rates = 0, unbacked = 0, checked = 0, excluded = 0 } = response.body || {};
        // `unbacked` is the answer to "why didn't this SKU change?" — say it up front
        // rather than leaving it to be inferred from a container count of 0 in the table.
        const noContainers = unbacked > 0 ? `, ${unbacked} with no container in this window` : '';
        const placeholders = excluded > 0 ? `, ${excluded} skipped for placeholder costs` : '';
        setState(
          containers === 0
            ? `no costed containers received in that window (${checked} checked${placeholders})`
            : `${containers} container${containers === 1 ? '' : 's'}, ${rates} rate${rates === 1 ? '' : 's'}${noContainers}${placeholders}`,
        );
        onRecalculated();
      })
      .catch((err) => setState(`failed (${err?.status || 'error'})`));
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent className="sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Recalculate COGS</DialogTitle>
        </DialogHeader>

        <p className="text-muted-foreground">
          Re-costs every container received in the window and re-averages every rate over it, so
          the whole table describes one window. A SKU with no container received in it shows 0.00
          across its rates and a window of N/A. Containers with placeholder costs (freight or local
          cost missing, both zero, or 1) are left out.
        </p>

        <div className="flex flex-col gap-2">
          <Label>Quick select</Label>
          <div className="flex gap-2">
            {WINDOW_PRESETS.map((months) => (
              <Button
                key={months}
                type="button"
                variant={activePreset === months ? 'primary' : 'secondary'}
                onClick={() => setRange(presetWindow(months))}
              >
                Last {months} months
              </Button>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <Label className="control-label">WINDOW FROM</Label>
            <Input
              type="date"
              className="form-control"
              value={range.from}
              max={range.to || undefined}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            />
          </div>
          <div className="flex-1">
            <Label className="control-label">WINDOW TO</Label>
            <Input
              type="date"
              className="form-control"
              value={range.to}
              min={range.from || undefined}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            />
          </div>
        </div>

        {state !== 'idle' && state !== 'loading' && (
          <div className="text-muted-foreground">Recalculated — {state}</div>
        )}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={state === 'loading'}>
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!range.from || !range.to || state === 'loading'}
            onClick={handleRecalculate}
          >
            {state === 'loading' ? 'Recalculating…' : 'Recalculate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecalculateCogsDialog;
